import {
  ChevronDown,
  ChevronUp,
  Filter,
  FolderMinus,
  MailOpen,
  Sparkles,
  Trash2,
} from "lucide-react";
import type { FilterDto, MessageSummaryDto, Recipient, SenderFilterDto } from "../../../shared/types";
import { SWATCH_CLASSES, isImportantColor } from "../filterColors";
import { setMessageDragData } from "./dragMessage";

export interface RowAvatar {
  number: number;
  color: string;
  username: string;
}

/** Outlook-style stamp: today → time, last 7 days → "fr. 22:53", older → short date. */
function formatWhen(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  if (date.toDateString() === now.toDateString()) return time;
  const ageDays = (now.getTime() - date.getTime()) / 86_400_000;
  if (ageDays < 7) {
    return `${date.toLocaleDateString(undefined, { weekday: "short" })} ${time}`;
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString();
}

export function MessageListItem({
  message,
  selected,
  matchedFilters,
  senderMarks,
  avatar,
  onClick,
  onDelete,
  onMarkRead,
  onCreateFilterFromSender,
  onAddToAiGroup,
  onToggleThread,
  threadExpanded,
  onUnassign,
  dragAccountId,
  dragIds,
  selectMode,
  checked,
  onToggleChecked,
}: {
  message: MessageSummaryDto;
  selected: boolean;
  matchedFilters: FilterDto[];
  /** Sender filters whose addresses include this message's sender (square marks). */
  senderMarks?: SenderFilterDto[];
  avatar?: RowAvatar;
  onClick: (e: React.MouseEvent) => void;
  onDelete?: () => void;
  /** Mark this (unread) email as read without opening it. */
  onMarkRead?: () => void;
  /** Open the create-filter dialog pre-filled with this message's sender. */
  onCreateFilterFromSender?: (from: Recipient) => void;
  /** Pin this email to an AI group (manual category). */
  onAddToAiGroup?: (message: MessageSummaryDto) => void;
  /** Expand/collapse the conversation thread under this row. */
  onToggleThread?: (message: MessageSummaryDto) => void;
  threadExpanded?: boolean;
  /** Remove this mail from the virtual folder it is shown in. */
  onUnassign?: () => void;
  /** When set, the row is draggable (drop onto a sidebar folder to move). */
  dragAccountId?: number;
  /** Ids the drag carries — a checked row drags the whole selection. */
  dragIds?: string[];
  selectMode?: boolean;
  checked?: boolean;
  onToggleChecked?: () => void;
}) {
  const unread = !message.isRead;
  const important = matchedFilters.some((f) => isImportantColor(f.color));
  const draggable = dragAccountId !== undefined;
  return (
    <button
      onClick={onClick}
      draggable={draggable}
      onDragStart={
        draggable
          ? (e) => setMessageDragData(e, dragIds?.length ? dragIds : [message.id], dragAccountId)
          : undefined
      }
      className={`group relative flex w-full flex-col border-b border-stroke pb-1.5 text-left hover:bg-black/[.03] ${
        selected ? "bg-accent-subtle" : important ? "bg-red-50" : "bg-white"
      }`}
    >
      {selected && <span className="absolute inset-y-0 left-0 w-[3px] bg-accent" />}
      {!selected && important && <span className="absolute inset-y-0 left-0 w-[3px] bg-red-600" />}

      {/* Header row: sender + time on their own line, separated from the body. */}
      <span className="flex w-full items-center justify-between gap-2 border-b border-stroke bg-black/[.04] px-3 py-1.5">
        <span className="flex min-w-0 items-center gap-1.5">
          {unread &&
            (onMarkRead ? (
              <span
                role="button"
                aria-label="Mark as read"
                title="Mark as read"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead();
                }}
                className="grid size-4 shrink-0 place-items-center rounded text-accent hover:bg-black/10"
              >
                <MailOpen size={13} />
              </span>
            ) : (
              <span title="Unread" className="size-2 shrink-0 rounded-full bg-accent" />
            ))}
          <span className={`truncate text-[13px] ${unread ? "font-semibold" : ""}`}>
            {message.from?.name ?? "Unknown sender"}
          </span>
        </span>
        {/* Hidden while hovering — the action icons appear in its corner. */}
        <span className="shrink-0 text-[11px] text-ink-muted group-hover:hidden">
          {formatWhen(message.receivedDateTime)}
        </span>
      </span>

      <span className="mt-1 flex w-full items-start gap-2 px-2">
      {/* Avatar slot: account number circle (checkbox in select mode), with the
          matched AI-group filters as small colored dots underneath. */}
      <span className="mt-0.5 flex w-8 shrink-0 flex-col items-center gap-1">
        {selectMode ? (
          <span className="relative -left-1 -top-1 grid size-8 place-items-center">
            <input
              type="checkbox"
              checked={checked ?? false}
              aria-label={`Select "${message.subject}"`}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleChecked?.()}
              className="size-4 accent-accent"
            />
          </span>
        ) : (
          <span
            title={avatar?.username}
            style={avatar ? { backgroundColor: avatar.color } : undefined}
            className="grid size-8 place-items-center rounded-full bg-accent-subtle text-[13px] font-bold text-ink"
          >
            {avatar?.number ?? "•"}
          </span>
        )}
        <span
          className={`flex max-w-8 flex-wrap justify-center gap-0.5 ${
            selectMode ? "relative -left-1 -top-1" : ""
          }`}
        >
          {matchedFilters.length > 0 ? (
            matchedFilters.map((f) => (
              <span
                key={f.id}
                title={f.name}
                className={`size-2 rounded-full ${SWATCH_CLASSES[f.color]}`}
              />
            ))
          ) : (
            // No AI group → gray, mirroring the sidebar's "Other" bucket.
            <span title="Other (no AI group)" className={`size-2 rounded-full ${SWATCH_CLASSES.gray}`} />
          )}
        </span>
        {/* Sender filters on their own row below the AI dots, as squares. */}
        {senderMarks && senderMarks.length > 0 && (
          <span
            className={`flex max-w-8 flex-wrap justify-center gap-0.5 ${
              selectMode ? "relative -left-1 -top-1" : ""
            }`}
          >
            {senderMarks.map((f) => (
              <span
                key={`s${f.id}`}
                title={f.name}
                className={`size-2 rounded-[3px] ${SWATCH_CLASSES[f.color]}`}
              />
            ))}
          </span>
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          {message.importance === "high" && (
            <span className="text-[12px] font-bold text-red-600" title="High importance">
              !
            </span>
          )}
          <span className={`truncate text-[13px] ${unread ? "text-accent" : "text-ink"}`}>
            {message.subject}
          </span>
          {message.hasAttachments && (
            <span className="text-[11px] text-ink-muted" title="Has attachments">
              📎
            </span>
          )}
        </span>
        <span className="block truncate text-[12px] text-ink-muted">{message.bodyPreview}</span>
      </span>
      </span>

      {onToggleThread && (
        <span
          role="button"
          aria-label="Show conversation"
          title={threadExpanded ? "Hide conversation" : "Show conversation"}
          onClick={(e) => {
            e.stopPropagation();
            onToggleThread(message);
          }}
          className={`absolute right-[5.5rem] top-1 z-10 size-6 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-accent ${
            threadExpanded ? "grid text-accent" : "hidden group-hover:grid"
          }`}
        >
          {threadExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      )}
      {onAddToAiGroup && (
        <span
          role="button"
          aria-label="Add email to AI category"
          title="Add to AI category"
          onClick={(e) => {
            e.stopPropagation();
            onAddToAiGroup(message);
          }}
          className="absolute right-[3.75rem] top-1 z-10 hidden size-6 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-accent group-hover:grid"
        >
          <Sparkles size={14} />
        </span>
      )}
      {onUnassign && (
        <span
          role="button"
          aria-label="Remove from this folder"
          title="Remove from this folder (back to inbox)"
          onClick={(e) => {
            e.stopPropagation();
            onUnassign();
          }}
          className="absolute right-8 top-1 z-10 hidden size-6 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-accent group-hover:grid"
        >
          <FolderMinus size={14} />
        </span>
      )}
      {onCreateFilterFromSender && message.from && (
        <span
          role="button"
          aria-label="Create a filter from this sender"
          title="Filter by this sender"
          onClick={(e) => {
            e.stopPropagation();
            onCreateFilterFromSender(message.from!);
          }}
          className="absolute right-8 top-1 z-10 hidden size-6 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-accent group-hover:grid"
        >
          <Filter size={14} />
        </span>
      )}
      {onDelete && (
        <span
          role="button"
          aria-label="Delete this email"
          title="Delete (move to Trash)"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute right-1 top-1 z-10 hidden size-6 place-items-center rounded text-ink-muted hover:bg-black/10 hover:text-red-600 group-hover:grid"
        >
          <Trash2 size={14} />
        </span>
      )}
    </button>
  );
}
